import { forwardRef, useEffect, useMemo, useState } from "react";
import { BaseSelectRef } from "rc-select/lib/BaseSelect";

import {
  SelectedOptionWithSlider,
  SliderDropdownProps,
  SliderFormValueOption,
  SliderSelectedOption,
} from "./types";
import BaseSlider from "../Slider/BaseSlider";
import BaseError from "../BaseError";
import CustomChoiceDropdown from "./CustomChoiceDropdown";
import BaseDropdown from "./BaseDropdown";

import "./styles.css";

const SliderDropdown = forwardRef<BaseSelectRef, SliderDropdownProps>(
  (
    {
      value,
      onChange,
      options,
      multiple,
      customInputOptions,
      helpText,
      error,
      secondaryPromptText,
      allowClear,
      ...props
    }: SliderDropdownProps,
    ref,
  ) => {
    const [selectedOptions, setSelectedOptions] = useState<
      SliderSelectedOption[]
    >([]);

    useEffect(() => {
      if (!value) {
        setSelectedOptions([]);
      } else if (selectedOptions) {
        setSelectedOptions(
          value.map((i: SliderFormValueOption) => {
            const selectedOption = options.find(
              (option) => option.value === i.choice,
            );
            return {
              ...i,
              label: selectedOption?.label,
            };
          }),
        );
      }
    }, [value, options]);

    const handleChange = (values: (SliderFormValueOption | string)[]) => {
      const previousValues = Array.isArray(value) ? value : [];
      const selectedValues = values.map((value) => {
        const choiceValue = typeof value === "object" ? value?.choice : value;
        const previousValue = previousValues.find(
          (v) => (typeof v === "object" ? v.choice : v) === choiceValue,
        );
        const selectedOption = options.find((i) => i.value === choiceValue);

        if (typeof value === "object" && value !== null) {
          return {
            choice: value.choice,
            scale: previousValue?.scale,
            label: selectedOption?.label || "",
          };
        }

        // Auto-select scale if only one option available
        const relatedOptions = selectedOption?.relatedOptions;
        const autoScale =
          relatedOptions && relatedOptions.length === 1
            ? relatedOptions[0]?.value
            : undefined;

        return {
          choice: value,
          scale: autoScale,
          label: selectedOption?.label || "",
        };
      });

      setSelectedOptions(selectedValues);

      const formValues = selectedValues.map(({ choice, scale }) => ({
        choice,
        scale,
      }));

      onChange(multiple ? formValues : formValues[0]);
    };

    const handleSliderChange = (
      scaleValue: number,
      choiceValue: number | string,
    ) => {
      const updatedOptions = selectedOptions.map((opt) =>
        opt.choice === choiceValue ? { ...opt, scale: scaleValue } : opt,
      );
      setSelectedOptions(updatedOptions);
      if (multiple) {
        onChange(updatedOptions);
      } else {
        onChange({
          choice: choiceValue,
          scale: scaleValue,
        });
      }
    };

    const DropdownComponent = customInputOptions?.show
      ? CustomChoiceDropdown
      : BaseDropdown;

    const sortOptionsByChoice = (options: SliderSelectedOption[]) => {
      return options.sort((a, b) => {
        if (typeof a.choice === "number" && typeof b.choice === "number") {
          return a.choice - b.choice;
        }
        return String(a.choice).localeCompare(String(b.choice));
      });
    };

    const selectedOptionWithSlider = useMemo(() => {
      const shouldShowSliders = selectedOptions.every((option) => {
        const sliderOptions = options.find(
          (i) => i.value === option.choice,
        )?.relatedOptions;

        return sliderOptions && sliderOptions.length >= 2;
      });

      if (!shouldShowSliders) {
        return [];
      }

      return sortOptionsByChoice(selectedOptions).reduce<
        SelectedOptionWithSlider[]
      >((acc, option) => {
        const sliderOptions = options.find(
          (i) => i.value === option.choice,
        )?.relatedOptions;

        if (sliderOptions != null && sliderOptions.length != 0) {
          acc.push({
            ...option,
            sliderOptions,
          });
        }
        return acc;
      }, []);
    }, [customInputOptions, selectedOptions]);

    return (
      <div className="flex flex-col gap-400">
        <DropdownComponent
          value={value}
          onChange={handleChange}
          options={options}
          multiple={multiple}
          allowClear={allowClear}
          {...props}
          ref={ref}
          error={error}
          showError={false}
        />
        <BaseError
          error={
            Array.isArray(error) ? error.filter((i) => i != null)[0] : error
          }
        />
        {selectedOptionWithSlider.length > 0 && (
          <div className="flex flex-col gap-400">
            {helpText && (
              <div className="slider-dropdown-help-text">
                <p className="body-s-medium">{helpText}</p>
              </div>
            )}
            {selectedOptionWithSlider.map((option) => {
              if (option.sliderOptions.length < 2) {
                return;
              }

              return (
                <div
                  key={option.choice}
                  className="slider-dropdown-slider-container flex flex-col gap-100 self-center justify-self-center"
                >
                  <div className="text-sm font-medium">
                    {secondaryPromptText
                      ? secondaryPromptText.replace("{}", option.label)
                      : option.label}
                  </div>

                  <BaseSlider
                    options={option.sliderOptions}
                    value={option.scale}
                    onChange={(value) =>
                      handleSliderChange(value, option.choice)
                    }
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);

export default SliderDropdown;
